import type { PeriodWindow, TrendPoint } from "@/analytics/core/types";
import { toIsoDate } from "@/analytics/core/period-windows";
import { resolveMetricValue, type MetricScopeContext } from "@/analytics/core/resolve-metric";
import type { MetricDefinition } from "@/grid/core/types";
import type { AnalyticsRawData } from "@/analytics/core/types";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  format,
  parseISO,
  startOfMonth,
  startOfWeek,
} from "date-fns";

export type ChartBucketGrain = "day" | "week" | "month";

export interface ChartBucket {
  from: string;
  to: string;
  label: string;
}

function isDailyMetric(metric: MetricDefinition): boolean {
  return metric.source.kind === "daily-sum";
}

/** Chart bucket size depends on both period grain and how the metric is stored. */
export function chartBucketGrain(period: PeriodWindow, metric: MetricDefinition): ChartBucketGrain {
  if (isDailyMetric(metric)) {
    return period.grain === "quarter" || period.grain === "year" ? "week" : "day";
  }

  // team-period (sales, opps): stored as week or month rows, never daily
  if (period.grain === "quarter" || period.grain === "year") return "month";
  if (period.grain === "month") return "week";
  return "week";
}

function weekBucketsInRange(from: string, to: string): ChartBucket[] {
  const buckets: ChartBucket[] = [];
  let cursor = startOfWeek(parseISO(from), { weekStartsOn: 1 });

  while (toIsoDate(cursor) <= to) {
    const bucketFrom = toIsoDate(cursor);
    const bucketTo = toIsoDate(addDays(cursor, 6));
    const clippedTo = bucketTo > to ? to : bucketTo;
    buckets.push({
      from: bucketFrom,
      to: clippedTo,
      label: format(cursor, "MMM d"),
    });
    cursor = addDays(cursor, 7);
  }

  return buckets;
}

function monthBucketsInRange(from: string, to: string): ChartBucket[] {
  const buckets: ChartBucket[] = [];
  let cursor = startOfMonth(parseISO(from));
  const end = parseISO(to);

  while (cursor <= end) {
    const bucketFrom = toIsoDate(cursor);
    buckets.push({
      from: bucketFrom,
      to: bucketFrom,
      label: format(cursor, "MMM"),
    });
    cursor = addMonths(cursor, 1);
  }

  return buckets;
}

function dayBucketsInRange(from: string, to: string): ChartBucket[] {
  const days = eachDayOfInterval({ start: parseISO(from), end: parseISO(to) });
  return days.map((day) => {
    const iso = toIsoDate(day);
    return {
      from: iso,
      to: iso,
      label: format(day, "EEE d"),
    };
  });
}

export function buildChartBuckets(period: PeriodWindow, metric: MetricDefinition): ChartBucket[] {
  const grain = chartBucketGrain(period, metric);
  if (grain === "month") return monthBucketsInRange(period.from, period.to);
  if (grain === "week") return weekBucketsInRange(period.from, period.to);
  return dayBucketsInRange(period.from, period.to);
}

function periodSliceForBucket(
  bucket: ChartBucket,
  bucketGrain: ChartBucketGrain,
  period: PeriodWindow,
): PeriodWindow {
  if (bucketGrain === "day") return { ...period, from: bucket.from, to: bucket.to };

  if (bucketGrain === "week") {
    return {
      ...period,
      grain: "week",
      periodStart: bucket.from,
      from: bucket.from,
      to: bucket.to,
    };
  }

  return {
    ...period,
    grain: "month",
    periodStart: bucket.from,
    from: bucket.from,
    to: bucket.to,
  };
}

export function bucketValueForMetric(
  metric: MetricDefinition,
  bucket: ChartBucket,
  period: PeriodWindow,
  ctx: MetricScopeContext,
  raw: AnalyticsRawData,
): number | null {
  const bucketGrain = chartBucketGrain(period, metric);
  const slice = periodSliceForBucket(bucket, bucketGrain, period);
  return resolveMetricValue(metric, bucket, slice, ctx, raw);
}

export function buildSeriesPoints(
  metric: MetricDefinition,
  buckets: ChartBucket[],
  period: PeriodWindow,
  ctx: MetricScopeContext,
  raw: AnalyticsRawData,
): TrendPoint[] {
  return buckets.map((bucket) => ({
    date: bucket.from,
    label: bucket.label,
    value: bucketValueForMetric(metric, bucket, period, ctx, raw) ?? 0,
  }));
}

/** Compare series uses the same bucket structure as the primary period. */
export function buildCompareSeriesPoints(
  metric: MetricDefinition,
  primaryPeriod: PeriodWindow,
  comparePeriod: PeriodWindow,
  ctx: MetricScopeContext,
  raw: AnalyticsRawData,
): TrendPoint[] {
  const primaryBuckets = buildChartBuckets(primaryPeriod, metric);
  const compareBuckets = buildChartBuckets(comparePeriod, metric);
  const count = Math.min(primaryBuckets.length, compareBuckets.length);

  return Array.from({ length: count }, (_, index) => {
    const primaryBucket = primaryBuckets[index]!;
    const compareBucket = compareBuckets[index]!;
    const value = bucketValueForMetric(metric, compareBucket, comparePeriod, ctx, raw) ?? 0;

    return {
      date: primaryBucket.from,
      label: primaryBucket.label,
      value,
    };
  });
}
